import {setCookie} from "../helpers/cookies";
import {createBootstrapToast} from "../lq_js_common/helpers/toasts";

const MIN_PERIOD_SECONDS = 30 * 60;
const MIN_PERIOD_MINUTES = MIN_PERIOD_SECONDS / 60;

function showMinPeriodToast(message) {
    try {
        const header = document.createElement("strong");
        header.className = "me-auto";
        header.textContent = "Time period";
        const body = document.createElement("div");
        body.textContent = message;
        createBootstrapToast(`timePeriodToast_${Date.now()}`, header, body);
    } catch (e) {
        // no-op
    }
}

function parsePositiveInteger(value, fallback) {
    const parsed = Number.parseInt(String(value), 10);
    if (Number.isFinite(parsed) && parsed > 0) {
        return parsed;
    }
    return fallback;
}

function enforceMinimumRelative(value, units) {
    const safeUnits = (units === "minutes" || units === "hours" || units === "days") ? units : "minutes";
    const intValue = parsePositiveInteger(value, safeUnits === "minutes" ? MIN_PERIOD_MINUTES : 1);

    // Enforce minimum while preserving the selected unit.
    let minForUnit = 1;
    if (safeUnits === "minutes") {
        minForUnit = MIN_PERIOD_MINUTES;
    }
    const clamped = Math.max(minForUnit, intValue);
    const adjusted = clamped !== intValue || safeUnits !== units;
    return { value: String(clamped), units: safeUnits, adjusted };
}

function enforceMinimumAbsolute(startIso, endIso) {
    const startMs = Date.parse(startIso);
    const endMs = Date.parse(endIso);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
        return { start: startIso, end: endIso, adjusted: false };
    }
    let start = startMs;
    let end = endMs;
    if (end < start) {
        const tmp = start;
        start = end;
        end = tmp;
    }
    const minMs = MIN_PERIOD_SECONDS * 1000;
    if ((end - start) < minMs) {
        start = end - minMs;
        return { start: new Date(start).toISOString(), end: new Date(end).toISOString(), adjusted: true };
    }
    return { start: new Date(start).toISOString(), end: new Date(end).toISOString(), adjusted: false };
}

export class TimePicker {
    constructor(id) {
        this.div = document.getElementById(id);
        this.div.classList.add("mb-2");

        // Setup times
        let timeText = localStorage.getItem("timeText");
        if (timeText !== null) {
            // Parse it from the JSON
            this.time = JSON.parse(timeText);
        } else {
            this.time = {
                mode: "relative",
                units: "hours",
                value: 24,
            };
        }
        if (this.time.mode === "relative" && typeof this.time.value !== "string") {
            this.time.value = String(this.time.value);
        }
        if (this.time.mode === "relative") {
            const normalized = enforceMinimumRelative(this.time.value, this.time.units);
            if (normalized.adjusted) {
                this.time.value = normalized.value;
                this.time.units = normalized.units;
                setTimeCookie(this.time);
                showMinPeriodToast(`Minimum time period is ${MIN_PERIOD_MINUTES} minutes.`);
            }
        } else if (this.time.mode === "absolute") {
            const normalized = enforceMinimumAbsolute(this.time.start, this.time.end);
            if (normalized.adjusted) {
                this.time.start = normalized.start;
                this.time.end = normalized.end;
                setTimeCookie(this.time);
                showMinPeriodToast(`Minimum time period is ${MIN_PERIOD_MINUTES} minutes.`);
            }
        }

        // Rewind Button
        let rewind = document.createElement("button");
        rewind.type = "button";
        rewind.classList.add("btn", "btn-outline-secondary", "btn-sm", "d-inline-flex", "align-items-center");
        let rewindIcon = document.createElement("i");
        rewindIcon.classList.add("fa-solid");
        rewindIcon.classList.add("fa-backward");
        rewind.appendChild(rewindIcon);
        let rewindLabel = document.createElement("span");
        rewindLabel.classList.add("ms-1", "small");
        rewindLabel.textContent = "Back";
        rewind.appendChild(rewindLabel);
        this.div.appendChild(rewind);

        // Forward Button
        let forward = document.createElement("button");
        forward.type = "button";
        forward.classList.add("btn", "btn-outline-secondary", "btn-sm", "ms-1", "d-inline-flex", "align-items-center");
        let forwardIcon = document.createElement("i");
        forwardIcon.classList.add("fa-solid");
        forwardIcon.classList.add("fa-forward");
        forward.appendChild(forwardIcon);
        let forwardLabel = document.createElement("span");
        forwardLabel.classList.add("ms-1", "small");
        forwardLabel.textContent = "Forward";
        forward.appendChild(forwardLabel);
        this.div.appendChild(forward);

        // Zoom In Button
        let zoomIn = document.createElement("button");
        zoomIn.type = "button";
        zoomIn.classList.add("btn", "btn-outline-secondary", "btn-sm", "ms-2", "d-inline-flex", "align-items-center");
        let zoomInIcon = document.createElement("i");
        zoomInIcon.classList.add("fa-solid");
        zoomInIcon.classList.add("fa-magnifying-glass-plus");
        zoomIn.appendChild(zoomInIcon);
        let zoomInLabel = document.createElement("span");
        zoomInLabel.classList.add("ms-1", "small");
        zoomInLabel.textContent = "Zoom In";
        zoomIn.appendChild(zoomInLabel);
        this.div.appendChild(zoomIn);

        // Zoom Out Button
        let zoomOut = document.createElement("button");
        zoomOut.type = "button";
        zoomOut.classList.add("btn", "btn-outline-secondary", "btn-sm", "ms-1", "d-inline-flex", "align-items-center");
        let zoomOutIcon = document.createElement("i");
        zoomOutIcon.classList.add("fa-solid");
        zoomOutIcon.classList.add("fa-magnifying-glass-minus");
        zoomOut.appendChild(zoomOutIcon);
        let zoomOutLabel = document.createElement("span");
        zoomOutLabel.classList.add("ms-1", "small");
        zoomOutLabel.textContent = "Zoom Out";
        zoomOut.appendChild(zoomOutLabel);
        this.div.appendChild(zoomOut);

        // Time Period Label
        let timeLabel = document.createElement("span");
        timeLabel.classList.add("text-body-secondary", "small", "ms-2", "me-1");
        timeLabel.textContent = "Time Period";
        this.div.appendChild(timeLabel);

        // Time Period Dropdown
        let timeDropdown = document.createElement("div");
        timeDropdown.classList.add("dropdown", "d-inline-block");

        let timeRange = document.createElement("button");
        timeRange.id = "timeRangeDropdown";
        timeRange.type = "button";
        timeRange.classList.add("btn", "btn-outline-secondary", "btn-sm", "dropdown-toggle");
        timeRange.setAttribute("data-bs-toggle", "dropdown");
        timeRange.setAttribute("aria-expanded", "false");
        timeRange.textContent = displayTimeRange(this.time);
        timeDropdown.appendChild(timeRange);

        let dropdownMenu = document.createElement("ul");
        dropdownMenu.classList.add("dropdown-menu");
        timeDropdown.appendChild(dropdownMenu);

        this.presetButtons = new Map();
        [
            {label: "Last 1 hour", value: "1", units: "hours"},
            {label: "Last 6 hours", value: "6", units: "hours"},
            {label: "Last 12 hours", value: "12", units: "hours"},
            {label: "Last 24 hours", value: "24", units: "hours"},
            {label: "Last 7 days", value: "7", units: "days"},
            {label: "Last 1 month", value: "28", units: "days"},
            {label: "Last 6 months", value: "183", units: "days"},
            {label: "Last 1 year", value: "365", units: "days"},
        ].forEach((preset) => {
            let item = document.createElement("li");
            let button = document.createElement("button");
            button.type = "button";
            button.classList.add("dropdown-item");
            button.textContent = preset.label;
            button.onclick = () => this.relativeTime(preset.value, preset.units);
            item.appendChild(button);
            dropdownMenu.appendChild(item);
            this.presetButtons.set(`${preset.value}:${preset.units}`, button);
        });

        let divider = document.createElement("li");
        divider.classList.add("dropdown-divider");
        dropdownMenu.appendChild(divider);

        let advancedItem = document.createElement("li");
        let advancedButton = document.createElement("button");
        advancedButton.type = "button";
        advancedButton.classList.add("dropdown-item");
        advancedButton.textContent = "Advanced...";
        advancedButton.onclick = () => this.openAdvancedModal();
        advancedItem.appendChild(advancedButton);
        dropdownMenu.appendChild(advancedItem);

        this.div.appendChild(timeDropdown);
        this.updatePresetHighlight();
    }

    wireupModal() {
        document.getElementById("gpCustom").onclick = () => {
            let customTime = document.getElementById("relativeNumber").value;
            let customUnits = document.getElementById("relativeUnit").value;
            this.relativeTime(customTime, customUnits);
        };

        document.getElementById("gpRangeSet").onclick = () => {
            let startDate = document.getElementById("graphStartDate").value;
            let startTime = document.getElementById("graphStartTime").value;
            let endDate = document.getElementById("graphEndDate").value;
            let endTime = document.getElementById("graphEndTime").value;
            let start = new Date(startDate + "T" + startTime).toISOString();
            let end = new Date(endDate + "T" + endTime).toISOString();

            this.absoluteTime(start, end);
        };
    }

    openAdvancedModal() {
        $("#timewarpModal").modal("show");
        this.wireupModal();
    }

    updatePresetHighlight() {
        if (!this.presetButtons) {
            return;
        }
        let activeKey = null;
        if (this.time.mode === "relative") {
            activeKey = `${this.time.value}:${this.time.units}`;
        }
        this.presetButtons.forEach((button, key) => {
            button.classList.toggle("active", key === activeKey);
        });
    }

    relativeTime(value, units) {
        const original = { value: String(value), units };
        const normalized = enforceMinimumRelative(original.value, original.units);
        this.time = {
            mode: "relative",
            value: normalized.value,
            units: normalized.units,
        };
        setTimeCookie(this.time);
        let timeRange = document.getElementById("timeRangeDropdown");
        timeRange.textContent = displayTimeRange(this.time);
        this.updatePresetHighlight();
        this.alertChildren();

        if (normalized.adjusted) {
            showMinPeriodToast(`Minimum time period is ${MIN_PERIOD_MINUTES} minutes.`);
        }
    }

    absoluteTime(start, end) {
        const normalized = enforceMinimumAbsolute(start, end);
        this.time = {
            mode: "absolute",
            start: normalized.start,
            end: normalized.end,
        };
        setTimeCookie(this.time);
        let timeRange = document.getElementById("timeRangeDropdown");
        timeRange.textContent = displayTimeRange(this.time);
        this.updatePresetHighlight();
        this.alertChildren();

        if (normalized.adjusted) {
            showMinPeriodToast(`Minimum time period is ${MIN_PERIOD_MINUTES} minutes.`);
        }
    }

    alertChildren() {
        if (window.timeGraphs !== undefined) {
            window.timeGraphs.forEach((graph) => {
                graph.onTimeChange();
            });
        }
    }
}

function displayTimeRange(time) {
    if (time.mode === "relative") {
        return `Last ${time.value} ${time.units}`;
    } else if (time.mode === "absolute") {
        return `${time.start} - ${time.end}`;
    }
}

function setTimeCookie(time) {
    // Convert time to JSON
    let timeText = JSON.stringify(time);
    // Set a cookie named "timePeriod" with the value of timeText
    setCookie("timePeriod", timeText, 365);
    localStorage.setItem("timeText", timeText);
}
